import type { ComponentPropsWithRef, ReactNode } from "react";

import type { SectionProps } from "@/common/components-2/section";
import { Section } from "@/common/components-2/section";
import { cn } from "@/services/styles/cn";

export interface NumbersSectionProps extends SectionProps {
  heading?: string | ReactNode;
}

export const NumbersSection = ({ className, children, heading, ...props }: NumbersSectionProps) => (
  <Section className={cn("bg-white-linen-100 py-6 md:py-12", className)} {...props}>
    {heading ? <h2 className="mb-11 text-center font-serif text-4xl lg:mb-14">{heading}</h2> : null}
    <div className="mx-auto flex w-fit flex-col items-center gap-y-8 md:gap-y-10 lg:w-full lg:flex-row lg:justify-between lg:gap-x-8 xl:justify-between">
      {children}
    </div>
  </Section>
);

export interface NumbersSectionItemProps extends Omit<ComponentPropsWithRef<"div">, "children"> {
  value: string;
  description: string;
  note?: string;
  classes?: {
    wrapper?: string;
    value?: string;
    descriptionWrapper?: string;
    description?: string;
    note?: string;
  };
}

export const NumbersSectionItem = ({
  className,
  value,
  description,
  note,
  classes,
  ...props
}: NumbersSectionItemProps) => (
  <div
    className={cn("flex gap-x-3 text-foreground lg:w-fit lg:flex-col xl:flex-row", classes?.wrapper, className)}
    {...props}
  >
    <p
      className={cn(
        "w-[105px] text-nowrap font-semibold font-serif text-3xl italic max-lg:text-right md:w-[175px] md:text-5xl lg:w-auto",
        classes?.value,
      )}
    >
      {value}
    </p>
    <div className={cn("flex flex-col", classes?.descriptionWrapper)}>
      <p className={cn("text-nowrap font-normal font-serif text-base italic md:text-2xl", classes?.description)}>
        {description}
      </p>
      {note ? (
        <p className={cn("font-medium font-sans text-stone-400 text-xs uppercase md:text-sm", classes?.note)}>{note}</p>
      ) : null}
    </div>
  </div>
);

export function NumbersSectionItem2({
  className,
  value,
  description,
  ...props
}: Omit<NumbersSectionItemProps, "note">) {
  return (
    <div
      className={cn(
        "flex w-full flex-wrap items-center gap-x-3 gap-y-1 lg:w-fit lg:flex-col lg:items-start xl:flex-row xl:items-center",
        className,
      )}
      {...props}
    >
      <p className="text-nowrap font-semibold font-serif text-3xl text-foreground italic md:text-5xl">{value}</p>
      <p className="text-nowrap font-normal font-serif text-base text-foreground italic md:text-2xl">{description}</p>
    </div>
  );
}
